import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import { AI_IMAGES } from "@/lib/aiImages";
import { useLanguage } from "@/context/LanguageContext";

export default function HumanAi() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const steps = t("home.humanSteps");

  return (
    <section ref={ref} className="py-24 border-t border-white/8 relative overflow-hidden" data-testid="human-ai-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SectionLabel>{t("home.humanLabel")}</SectionLabel>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {t("home.humanH2a")} <span className="vora-text-gradient">{t("home.humanAccent")}</span>
          </h2>
          <p className="mt-5 max-w-xl text-base text-slate-300 leading-relaxed">{t("home.humanBody")}</p>

          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {steps.map((s, i) => (
              <div key={s.t} className="flex items-center gap-3">
                <div className={`rounded-2xl px-5 py-4 flex-1 sm:flex-none ${i === 1 ? "bg-gradient-to-r from-[#4F8CFF] to-[#7C5CFF] text-white vora-glow-blue" : "vora-card"}`}>
                  <p className={`text-sm font-semibold ${i === 1 ? "text-white" : "text-white"}`}>{s.t}</p>
                  <p className={`text-xs mt-0.5 ${i === 1 ? "text-white/80" : "text-slate-400"}`}>{s.s}</p>
                </div>
                {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-[#4F8CFF] rotate-90 sm:rotate-0 shrink-0 self-center" />}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative">
            <div className="absolute -inset-6 bg-[#4F8CFF]/10 blur-3xl rounded-[40px]" aria-hidden />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)]">
              <motion.img
                src={AI_IMAGES.humanAi}
                alt="Human hand interacting with an intelligent holographic interface"
                className="w-full h-[320px] sm:h-[420px] object-cover scale-110"
                style={{ y }}
                loading="lazy"
                data-testid="human-ai-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05080D]/70 via-transparent to-transparent" aria-hidden />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
